import * as Yup from 'yup';

// Single source of truth for the Master Data Request module.
// Every page (new request, my list, detail, approval queue) reads its field list,
// status labels and validation from here so the two request types never drift apart.

export const REQUEST_TYPES = {
    ITEM: {
        value: 'ITEM',
        label: 'Item Request',
        variant: 'primary',
        nameField: { name: 'REQ_NAME', label: 'Product Name', type: 'text', required: true, maxLength: 200 },
        fields: [
            { name: 'ITEM_DESCRIP', label: 'Description', type: 'textarea', rows: 3 },
            { name: 'NATURE_OF_REQ', label: 'Nature of Requirement', type: 'textarea', rows: 3, maxLength: 500 },
            { name: 'CUS_CODE', label: 'Customer', type: 'select', source: 'customers' }
        ]
    },
    CUSTOMER: {
        value: 'CUSTOMER',
        label: 'Customer Request',
        variant: 'info',
        nameField: { name: 'REQ_NAME', label: 'Customer Name', type: 'text', required: true, maxLength: 200 },
        fields: [
            { name: 'CUS_ADDRESS', label: 'Address', type: 'textarea', rows: 3 },
            { name: 'GSTIN_NO', label: 'GSTIN', type: 'text', maxLength: 30, autoCapitalize: 'characters' },
            { name: 'EMAIL_ID', label: 'Email ID', type: 'email', maxLength: 100 },
            { name: 'CONTACT_PERSON', label: 'Contact Person', type: 'text', maxLength: 200 },
            { name: 'CONTACT_NO', label: 'Contact Number', type: 'tel', maxLength: 50 }
        ]
    }
};

// Rendered for both types, after the type specific fields.
export const COMMON_FIELDS = [
    { name: 'NOTE', label: 'Notes', type: 'textarea', rows: 3, maxLength: 500 }
];

// Rendered only in the approver view, when closing a request.
export const ADMIN_FIELDS = [
    { name: 'CreatedCode', label: 'Created Code', type: 'text', required: true, maxLength: 50 },
    { name: 'CreatedName', label: 'Created Name', type: 'text', maxLength: 200 }
];

// Status strings are exactly what the API stores - do not re-case them here.
export const REQUEST_STATUS = {
    Open: 'Open',
    Completed: 'Completed',
    Rejected: 'Rejected'
};

export const STATUS_CONFIG = {
    Open: { label: 'Open', variant: 'warning' },
    Completed: { label: 'Completed', variant: 'success' },
    Rejected: { label: 'Rejected', variant: 'danger' }
};

export const REQUEST_ACTIONS = {
    Complete: 'Complete',
    Reject: 'Reject'
};

export const getStatusConfig = (status) =>
    STATUS_CONFIG[status] || { label: status || '-', variant: 'secondary' };

export const getTypeConfig = (reqType) => REQUEST_TYPES[reqType] || REQUEST_TYPES.ITEM;

export const getTypeLabel = (reqType) => getTypeConfig(reqType).label;

export const getTypeVariant = (reqType) => getTypeConfig(reqType).variant;

// The type selector options, derived so a new type never needs a second list.
export const REQUEST_TYPE_OPTIONS = Object.values(REQUEST_TYPES).map(t => ({
    value: t.value,
    label: t.label
}));

// All fields of a type, in display order.
export const getFieldList = (reqType) => {
    const config = getTypeConfig(reqType);
    return [config.nameField, ...config.fields, ...COMMON_FIELDS];
};

// Empty values for exactly the fields of the chosen type - fields belonging to the
// other type are never present, so they cannot be submitted by accident.
export const getInitialValues = (reqType) =>
    getFieldList(reqType).reduce((values, field) => {
        values[field.name] = '';
        return values;
    }, {});

const buildFieldValidator = (field) => {
    let validator = Yup.string();

    if (field.maxLength) {
        validator = validator.max(field.maxLength, `${field.label} cannot exceed ${field.maxLength} characters`);
    }
    if (field.type === 'email') {
        validator = validator.email('Enter a valid email address');
    }
    if (field.required) {
        validator = validator.trim().required(`${field.label} is required`);
    }

    return validator;
};

export const buildSchemaForFields = (fields) =>
    Yup.object().shape(
        fields.reduce((shape, field) => {
            shape[field.name] = buildFieldValidator(field);
            return shape;
        }, {})
    );

export const buildValidationSchema = (reqType) => buildSchemaForFields(getFieldList(reqType));

export const getEmptyValues = (fields) =>
    fields.reduce((values, field) => {
        values[field.name] = '';
        return values;
    }, {});

// What the reviewer fills in for each action. Complete needs the code of the record
// they created in the ERP; Reject needs a reason. Both match what the API enforces,
// so the client and the server can never disagree about which field is mandatory.
export const ACTION_FIELDS = {
    [REQUEST_ACTIONS.Complete]: [
        ...ADMIN_FIELDS,
        { name: 'Comment', label: 'Comment (optional)', type: 'textarea', rows: 3, maxLength: 500 }
    ],
    [REQUEST_ACTIONS.Reject]: [
        { name: 'Comment', label: 'Reason for Rejection', type: 'textarea', rows: 3, maxLength: 500, required: true }
    ]
};

// The reviewer gate, mirroring MasterRequestController.GetReviewerAuth: either role
// is enough. Used to hide the action panel on the shared detail page and the sidebar
// entry; the route guard and the API enforce it too.
export const REVIEWER_ROLES = ['Admin', 'Coordinator'];

export const isMasterRequestReviewer = () => {
    const roles = (sessionStorage.getItem('userrole') || '')
        .split(',')
        .map(r => r.trim())
        .filter(Boolean);

    return REVIEWER_ROLES.some(role => roles.includes(role));
};
